import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatSnackBar } from '@angular/material';
import { FlightComm, FrequencyMode } from '../models/flight-comm.model';
import { CommunicationService } from '../service/communication.service';
import { formatPhoneString, undoPhoneFormat } from '../../util/phone';

@Component({
  selector: 'app-edit-communication',
  templateUrl: './edit-communication.component.html',
  styleUrls: ['./edit-communication.component.scss']
})
export class EditCommunicationComponent implements OnInit {
  comm: FlightComm;
  origComm: FlightComm;
  isNew = false;
  isLoading = true;
  airportId: number;
  formGroup: FormGroup;
  selectedPhone: string;
  selectedFreqMode: string;
  selectedNewFreqMode: string = null;
  selectedNewFrequency: string = null;
  selectedNewPhone: string = null;
  selectedNewSector: string = null;
  newFrequencyText = 'Expand to add new frequency';
  newFrequencyPanelIsOpen = false;

  constructor(private _matDialogRef: MatDialogRef<EditCommunicationComponent>,
              public _snackBar: MatSnackBar,
              @Inject(MAT_DIALOG_DATA) data,
              private _commService: CommunicationService) {
    this.comm = data.comm || new FlightComm();
    this.isNew = data.isNew || false;
    this.airportId = data.airportId || data.comm.airportId;
    this.origComm = Object.assign(new FlightComm(), data.comm);
    if (this.comm.phoneNumber) {
      this.selectedPhone = formatPhoneString(this.comm.phoneNumber);
    } else {
      this.selectedPhone = null;
    }
    if (this.isNew) {
      this.selectedFreqMode = null;
    } else {
      this.selectedFreqMode = this.getFreqMode(this.comm.freqMode);
    }
  }

  ngOnInit() {
    this._matDialogRef.backdropClick().subscribe(() => {
      this.close({cancelBtnClicked: true});
    });
    this.formGroup = new FormGroup({
      type: new FormControl(this.comm.service, [Validators.required, Validators.maxLength(50)]),
      callSign: new FormControl(this.comm.radioCall, [Validators.maxLength(100)]),
      frequency: new FormControl(this.comm.frequency, [Validators.pattern('^-?\\d{0,6}\\.?\\d{0,3}$')]),
      freqMode: new FormControl(this.selectedFreqMode, [Validators.required]),
      phone: new FormControl(formatPhoneString(this.selectedPhone), [Validators.pattern('^([(]\\d{3}[)]\\s\\d{3}[-]\\d{4})$')]),
      sector: new FormControl(this.comm.sector, [Validators.maxLength(200)]),
      newFrequency: new FormControl('', [Validators.pattern('^-?\\d{0,6}\\.?\\d{0,3}$')]),
      newFreqMode: new FormControl(),
      newPhone: new FormControl('', [Validators.pattern('^([(]\\d{3}[)]\\s\\d{3}[-]\\d{4})$')]),
      newSector: new FormControl('', [Validators.maxLength(200)])
    });

    this.isLoading = false;
  }

  getFreqMode(freqMode: FrequencyMode): string {
    switch (freqMode) {
      case FrequencyMode.Duplex:
        return 'Duplex';
      case FrequencyMode.Receive:
        return 'Receive';
      case FrequencyMode.Transmit:
        return 'Transmit';
      case FrequencyMode.Unknown:
        return 'Unknown';
      default:
        return '';
    }
  }

  setFreqMode(freqMode: string): FrequencyMode {
    switch (freqMode) {
      case 'Duplex':
        return FrequencyMode.Duplex;
      case 'Receive':
        return FrequencyMode.Receive;
      case 'Transmit':
        return FrequencyMode.Transmit;
      case 'Unknown':
        return FrequencyMode.Unknown;
    }
  }

  newFrequencyPanelOpened() {
    this.newFrequencyText = 'Enter new frequency info';
    this.newFrequencyPanelIsOpen = true;
    this.formGroup.controls['newFreqMode'].setValidators(Validators.required);
    this.formGroup.controls['newFreqMode'].updateValueAndValidity();
  }

  newFrequencyPanelClosed() {
    this.newFrequencyText = 'Expand to add new frequency';
    this.newFrequencyPanelIsOpen = false;
    this.formGroup.controls['newFreqMode'].clearValidators();
    this.formGroup.controls['newFreqMode'].updateValueAndValidity();
  }

  /**
   * formats passed in phone number string;
   * @param {string} field
   * @param {string} value
   * @return {string}
   */
  formatPhoneNumber (field: string, value: string): string {
    const NEW_NUMBER: string = formatPhoneString(value);
    this.formGroup.patchValue({[field]: NEW_NUMBER});
    return NEW_NUMBER;
  }

  /**
   * event triggered by keyup event on phone number or fax fields
   * @param event
   * @param {string} field
   * @param phoneInputObj
   */
  phoneFieldKeyUp (event, field: string, phoneInputObj) {
    const value: string = this.formGroup.get(field).value;
    const rawValue = undoPhoneFormat(value);
    const KEY = event.key;

    if (KEY === 'Backspace' && rawValue.length === 10 && phoneInputObj.selectionStart === rawValue.length) {
      this.formatPhoneNumber(field, value);
    } else if (phoneInputObj.selectionStart < value.length) {
    } else if (KEY === 'Backspace' || KEY === 'ArrowLeft' || KEY === 'ArrowRight') {
      return;
    } else {
      this.formatPhoneNumber(field, value);
    }
  }

  /**
   * triggered when phone number or fax field goes out of focus
   * @param {string} field
   */
  phoneFieldBlur(field: string) {
    const VALUE: string = this.formGroup.get(field).value;
    if (VALUE === '') {
      return;
    }
    this.formatPhoneNumber(field, VALUE);
  }

  /**
   * triggered by save button click event
   * @return {Promise<void>}
   */
  async save() {
    try {
      let commResult: any;
      let freqResult: any;
      let newFreqResult: any;
      this.comm.service = this.formGroup.value.type;
      this.comm.radioCall = this.formGroup.value.callSign;
      this.comm.frequency = this.formGroup.value.frequency;
      this.comm.freqMode = this.setFreqMode(this.formGroup.value.freqMode);
      this.comm.phoneNumber = undoPhoneFormat(this.formGroup.value.phone);
      this.comm.sector = this.formGroup.value.sector;

      let newFreq: FlightComm = null;
      let newComm: FlightComm = null;
      if (this.newFrequencyPanelIsOpen) {
        newFreq = new FlightComm();
        newFreq.frequency = this.formGroup.value.newFrequency;
        newFreq.freqMode = this.setFreqMode(this.formGroup.value.newFreqMode);
        newFreq.phoneNumber = undoPhoneFormat(this.formGroup.value.newPhone);
        newFreq.sector = this.formGroup.value.sector;
        newFreq.flightCommId = this.comm.flightCommId;
      }

      if (this.isNew) {
        commResult = await this._commService.postComm(this.airportId, this.comm);
        this.comm.flightCommId = commResult.flightCommId;
        freqResult = await this._commService.postFrequency(this.airportId, this.comm);
        this.comm = this.combineCommAndFreqResults(commResult, freqResult);
      } else {
        commResult = await this._commService.putComm(this.airportId, this.comm);
        freqResult = await this._commService.putFrequency(this.airportId, this.comm);
        this.comm = this.combineCommAndFreqResults(commResult, freqResult);
        if (newFreq != null) {
          newFreqResult = await this._commService.postFrequency(this.airportId, newFreq);
          newComm = this.combineCommAndFreqResults(commResult, newFreqResult);
        }
      }
      this.close({cancelBtnClicked: false, success: true, comm: this.comm, newComm: newComm});
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false, error: e.error});
    }
  }

  combineCommAndFreqResults(comm: any, freq: any): FlightComm {
    const flightCommReturn = new FlightComm();
    flightCommReturn.flightCommId = comm.flightCommId;
    flightCommReturn.service = comm.service;
    flightCommReturn.shortName = comm.shortName;
    flightCommReturn.radioCall = comm.radioCall;
    flightCommReturn.frequencyId = freq.frequencyId;
    flightCommReturn.frequency = freq.frequency;
    flightCommReturn.freqMode = this.setFreqMode(freq.freqMode);
    flightCommReturn.phoneNumber = freq.phoneNumber;
    flightCommReturn.sector = freq.sector;
    return flightCommReturn;
  }

  /**
   * triggered by cancel button click event
   */
  cancel () {
    this.close({cancelBtnClicked: true});
  }

  /**
   * closes modal
   * @param data
   */
  close(data: any) {
    if (data && data.cancelBtnClicked) {
      Object.assign(this.comm, this.origComm);
    }
    data['isNew'] = this.isNew;
    this._matDialogRef.close(data);
  }
}
